import { NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ResolveLibModule } from 'resolve-lib';
import { MenuOption, OptionGroup } from '../../models/menu-option';
import { DropdownComponent } from '../dropdown/dropdown.component';
import { MenuInputComponent } from '../menu-input/menu-input.component';

@Component({
    selector: 'app-grouped-multiselect-search-menu',
    templateUrl: './grouped-multiselect-search-menu.component.html',
    styleUrls: ['./grouped-multiselect-search-menu.component.scss'],
    imports: [
        DropdownComponent,
        MenuInputComponent,
        ResolveLibModule,
        NgFor,
        NgIf,
    ]
})
export class GroupedMultiselectSearchMenuComponent implements OnInit {
  @Input() inputId?: string;
  @Input() name?: string;
  @Input() placeholder?: string;
  @Input() label?: string;
  @Input() isFullWidth?: boolean;
  @Input() isInlineTitle?: boolean;
  @Input() isInvalid?: boolean;
  @Input() isDisabled?: boolean;
  @Input() isRequired?: boolean;
  @Input() optionGroups: OptionGroup[] = [];
  @Input() disabledGroup: OptionGroup | null = null;
  @Input() disabledLabel: string = 'Disabled Options';
  @Input() selectedOptions: MenuOption[] = [];
  @Output() optionsSelected = new EventEmitter<MenuOption[]>();
  @Output() touched = new EventEmitter<void>();

  /** Denotes whether this component is a multi-select menu. */
  isMultiSelect: boolean = true;

  /** Denotes whether the user is filtering the menu. */
  isFiltering: boolean = false;

  /** Denotes whether this component is a searchable menu. */
  isSearch: boolean = true;

  /** This will either contain all options or
   * options filtered by the user's input. */
  menuOptionGroups?: OptionGroup[];

  ngOnInit(): void {
    this.#setMenuOptionGroups();
  }

  /**
   * This method is called when the user has
   * clicked outside of dropdown.
   */
  onDoneFiltering(): void {
    this.#setIsFiltering(false);
    this.#setMenuOptionGroups();
  }

  /**
   * The executed process when the user is opening the dropdown.
   */
  onOpeningMenu(): void {
    this.#setMenuOptionGroups();
    this.touched.emit();
  }

  /**
   * The method that is executed when the user is
   * typing in the searchable field.
   * @param searchValue the value input by user.
   */
  onSearchValueUpdated(searchValue: string): void {
    this.#setIsFiltering(true);
    this.#filterOptions(searchValue);
  }

  /**
   * This method is called when the user has
   * selected an option from the dropdown.
   * @param option MenuOption
   */
  onSelectionChange(option: MenuOption): void {
    const selections = this.#updateSelectedOptions(option);
    this.optionsSelected.emit(selections);
  }

  /**
   * The function executed when the dropdown is populated.
   * @param option The menu option.
   * @param groupName The group name.
   * @returns The unique id for the option.
   */
  getUniqueId(option: MenuOption, groupName?: string): string {
    const excludeCharacters = /[^\w]/g;
    const cleanLabel = groupName?.replace(excludeCharacters, '');
    const cleanValue = option.value?.replace(excludeCharacters, '');
    return `${cleanLabel}_${cleanValue}`;
  }

  /**
   * The function executed when the dropdown is populated; determining
   * whether the option is selected and therefore the checkbox
   * should be selected.
   * @param option The menu option.
   * @returns The determination whether the option is selected.
   */
  isOptionChecked(option: MenuOption): boolean {
    return !!this.selectedOptions.find(
      (selection) => selection.value === option.value
    );
  }

  /**
   * This method filters the dropdown options based on
   * the searchValue and applies html tag formatting
   * for user readability.
   * @param searchValue The user input.
   */
  #filterOptions(searchValue: string): void {
    this.menuOptionGroups = this.optionGroups.map((optionGroup) => {
      let filterGroup: OptionGroup = {
        name: optionGroup.name,
        options: optionGroup.options,
      };
      if (optionGroup.options) {
        filterGroup.options = optionGroup.options.map((option) => {
          let label = option.label;
          const index = label.toLowerCase().indexOf(searchValue.toLowerCase());
          const filteredOption = {
            id: option.id,
            value: option.value,
            label:
              index === -1
                ? option.label
                : `${option.label.substring(
                    0,
                    index
                  )}<b>${option.label.substring(
                    index,
                    index + searchValue.length
                  )}</b>${option.label.substring(index + searchValue.length)}`, //Case insensitive replace
          };
          return filteredOption;
        });
        filterGroup.options = filterGroup.options.filter((option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase())
        );
      }
      return filterGroup;
    });
  }

  /**
   * This function determines whether the option is selected and updates the selected options.
   * @param option The user's selected menu option.
   * @returns The selected menu options.
   */
  #updateSelectedOptions(option: MenuOption): MenuOption[] {
    if (this.isOptionChecked(option)) {
      return this.selectedOptions.filter(
        (selectedInput) => selectedInput.value !== option.value
      );
    }

    return [...this.selectedOptions, option];
  }

  /**
   * Sets the menu options.
   */
  #setMenuOptionGroups(): void {
    this.menuOptionGroups = this.optionGroups;
  }

  /**
   * Sets the isFiltering property.
   * @param value boolean
   */
  #setIsFiltering(value: boolean): void {
    this.isFiltering = value;
  }
}
